<template>
  <SearchLayout :open="open && !loading" @open="open = true" @close="open = false">
    <template #input>
      <FriedSearchInput
        ref="searchBoxRef"
        v-model="searchInput"
        :placeholder="t('search')"
        @keyup.enter="enter"
      >
        <template #right>
          <FriedRoundedButton
            v-if="searchInput.length || loading"
            :disabled="loading"
            :data-external="`${identifier}-search-clear-button`"
            @click="handleClear"
          >
            <template #icon>
              <FriedSpinner v-if="loading" :size="16" />
              <FriedIcon v-else icon="close" :size="IconSize.Small" />
            </template>
          </FriedRoundedButton>
        </template>
      </FriedSearchInput>
    </template>
    <template #content>
      <div>
        <FriedParagraph :size="ParagraphSize.Small" class="title">
          {{ t('previous_searches') }}
        </FriedParagraph>
        <template v-if="visibleHistory.length">
          <div v-for="entry in visibleHistory" :key="entry" class="history-entry-container">
            <FriedDropdownItem @click="handleSelectSearchHistoryEntry(entry)">
              <template #leftIcon>
                <FriedIcon icon="time-reverse" :size="IconSize.Small" />
              </template>
              <FriedParagraph>{{ entry }}</FriedParagraph>
            </FriedDropdownItem>
            <FriedIconButton
              :data-external="`${identifier}-search-remove-history-entry-button`"
              @click="removeHistoryEntry(entry)"
            >
              <FriedIcon :size="IconSize.Small" icon="close" />
            </FriedIconButton>
          </div>
        </template>
        <FriedParagraph v-else-if="!loading">
          {{ t('no_previous_searches') }}
        </FriedParagraph>
      </div>
    </template>
  </SearchLayout>
</template>

<script lang="ts">
import { IconSize, ParagraphSize, ButtonType } from '@getaccept/fried-tofu';
import { onMounted, ref, defineComponent, computed } from 'vue';
import useSearchHistory from '@getaccept/lib-shared-new/src/composables/search-history.composable';
import SearchLayout from '@getaccept/lib-shared-new/src/search/components/SearchLayout.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  components: {
    SearchLayout,
  },
  props: {
    loading: { type: Boolean },
    currentSearchTerm: { type: String, default: '' },
    identifier: {
      type: String,
      default: '',
    },
  },
  emits: ['update-search'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const searchBoxRef = ref(null);
    const searchInput = ref('');
    const open = ref(false);
    const visibleHistory = computed(() => history.value.slice(0, 5));

    const { history, add: addHistoryEntry, remove: removeHistoryEntry } = useSearchHistory(
      props.identifier
    );

    const handleClear = () => {
      searchInput.value = '';
      const inputElement = searchBoxRef.value?.$el.querySelector('input');
      inputElement?.focus();
      handleSearch();
    };

    const enter = () => {
      if (!searchInput.value) {
        handleSearch();
        return;
      }
      handleSearch();
      addHistoryEntry(searchInput.value);
      open.value = false;
    };

    const handleSearch = () => {
      emit('update-search', searchInput.value);
    };

    const handleSelectSearchHistoryEntry = (entry: string) => {
      searchInput.value = entry;
      handleSearch();
    };

    onMounted(() => {
      if (props.currentSearchTerm) {
        searchInput.value = props.currentSearchTerm;
      }
    });

    return {
      open,
      searchBoxRef,
      searchInput,
      visibleHistory,
      enter,
      handleClear,
      removeHistoryEntry,
      handleSelectSearchHistoryEntry,
      addHistoryEntry,
      ParagraphSize,
      IconSize,
      ButtonType,
      t,
      handleSearch,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/lib-shared-new/src/assets/scss/search-bar';
</style>
